import type { SliceCreator } from '../state';

/**
 * Boot, as an explicit state machine rather than a chain of effects.
 *
 * THIS IS THE PLAN'S "EFFECT-ORDERING HAZARDS TO ENCODE EXPLICITLY", built. CLAUDE.md
 * documents three ordering rules that are guaranteed today by nothing more than the order of
 * statements inside `enterApp()`:
 *
 *   1. `restoreViewState()` must run AFTER `loadMapFromServer()` — which re-collapses every
 *      node, silently undoing a restore that ran first — and AFTER `applyPermissions()`,
 *      which populates the hidden sets the restore validates ids against.
 *   2. A latch must suppress every view write until that read has happened, or the
 *      default-state setup that runs before it (`applyPermissions()` calling
 *      `setAiDocMode(false)`) persists an empty view over the one about to be loaded.
 *   3. Only the EXCEPTIONS to "collapsed" are stored, so a node added since the last visit
 *      defaults to collapsed.
 *
 * Under React effects those become implicit — an emergent property of effect scheduling,
 * which is not a thing anybody can read off the code or test directly. So the phase is DATA:
 * one value, advanced one step at a time, with the view-write guard reading it
 * (`viewWritesAllowed`) instead of a separate boolean nobody can trace.
 *
 * WHY `urlParsed` SITS WHERE IT DOES. After `permissionsApplied`, so an unreachable link is
 * detected before it is acted on — a hidden node must report "not permitted" rather than
 * leaking its existence through "not found". Before `viewRestored`, so a shared link's filters
 * take precedence and the restore knows not to overwrite them. That single ordering is what
 * makes shared links behave correctly. Stage 1b has not started, so nothing does work in that
 * phase yet; the phase exists anyway, because adding a step to the middle of a sequence later
 * is how orderings get broken.
 *
 * WHO DRIVES IT, TODAY. The vanilla script still owns boot, so it ANNOUNCES its phase and the
 * store RECORDS it — see `store/fromApp.ts`. Ownership inverts when the map ports and this
 * becomes the thing that actually sequences boot.
 */
export type BootPhase =
  | 'booting'
  | 'mapLoaded'
  | 'permissionsApplied'
  | 'urlParsed'
  | 'viewRestored'
  | 'ready';

/** The sequence. Index in this array IS the ordering; nothing else defines it. */
export const BOOT_ORDER: readonly BootPhase[] = [
  'booting',
  'mapLoaded',
  'permissionsApplied',
  'urlParsed',
  'viewRestored',
  'ready',
] as const;

export interface BootSlice {
  bootPhase: BootPhase;

  /**
   * Has the session question been ASKED yet?
   *
   * Not derivable from the phase, and the reason is the one case the phase does not cover: a
   * signed-out load never enters `enterApp()` at all, so it finishes boot still at `booting`.
   * Without this a region cannot tell "not signed in" from "not asked yet", and the sign-in
   * overlay flashes for somebody who is already signed in while the check is in flight. It is
   * the bridge's `booted`, under the name that says what it means.
   */
  sessionChecked: boolean;
  setSessionChecked: (checked: boolean) => void;

  /**
   * Advance exactly one step.
   *
   * Refuses anything else — a skip, a repeat, or a step backwards — and says so loudly rather
   * than silently accepting it, because an out-of-order boot is precisely the failure this
   * machine exists to make impossible. Returns whether the move was taken, so a caller that
   * genuinely wants to know can ask without reading the phase back.
   */
  enterBootPhase: (next: BootPhase) => boolean;

  /**
   * Back to the start, and the ONLY backwards move there is.
   *
   * A second person signing in on the same browser boots again from scratch: the vanilla
   * `enterApp()` sets `viewStateReady = false` for exactly this reason, so that one user
   * cannot inherit the other's "ready" state mid-login.
   */
  resetBoot: () => void;
}

/** Has boot reached at least this phase? The comparison every guard is written in terms of. */
export const bootAtLeast = (phase: BootPhase, target: BootPhase): boolean =>
  BOOT_ORDER.indexOf(phase) >= BOOT_ORDER.indexOf(target);

/**
 * May view state be written yet?
 *
 * The `viewStateReady` latch, as a derived fact rather than a second variable that can
 * disagree with the phase. Writing before `viewRestored` is what persists an empty view over
 * the one about to be read.
 */
export const viewWritesAllowed = (state: { bootPhase: BootPhase }): boolean =>
  bootAtLeast(state.bootPhase, 'viewRestored');

export const createBootSlice: SliceCreator<BootSlice> = (set, get) => ({
  bootPhase: 'booting',
  sessionChecked: false,

  setSessionChecked: (checked) => {
    if (get().sessionChecked === checked) return;
    set({ sessionChecked: checked });
  },

  enterBootPhase: (next) => {
    const current = get().bootPhase;
    if (BOOT_ORDER.indexOf(next) !== BOOT_ORDER.indexOf(current) + 1) {
      console.error(
        `[boot] refused ${current} -> ${next}: phases advance one step at a time, in order`,
      );
      return false;
    }
    set({ bootPhase: next });
    return true;
  },

  resetBoot: () => {
    if (get().bootPhase === 'booting') return;
    set({ bootPhase: 'booting' });
  },
});
